// ============================================================================
// Cart helpers + WhatsApp message building
// ----------------------------------------------------------------------------
// The cart STATE + actions live in CartContext (single shared instance).
// `useCart` is re-exported from there so existing imports keep working.
// This file holds the pure helper functions used to build the WhatsApp order.
// ============================================================================

import type { Cart, CartItem, SelectedModifier } from '@/types'
import { calculateItemPrice, sanitizePhoneNumber } from '@/lib/formatPrice'

export { useCart } from './CartContext'
export type { UseCartReturn } from './CartContext'

// ============================================================================
// Helper: Calculate item price with variants and modifiers
// ============================================================================

export function calculateCartItemPrice(
  basePrice: number,
  variant?: { price: number; price_adjustment?: number },
  modifiers?: SelectedModifier[]
): number {
  let price = basePrice

  if (variant?.price !== undefined) {
    price = variant.price
  } else if (variant?.price_adjustment !== undefined) {
    price += variant.price_adjustment
  }

  if (modifiers) {
    price += modifiers.reduce((sum, mod) => sum + mod.price, 0)
  }

  return Math.max(0, price)
}

// ============================================================================
// Helper: Format cart item for WhatsApp message
// ============================================================================

export function formatCartItemForWhatsApp(item: CartItem): string {
  let text = `${item.quantity}x ${item.name}`

  if (item.variant) {
    text += ` (${item.variant.name})`
  }

  if (item.modifiers && item.modifiers.length > 0) {
    const modifierNames = item.modifiers.map((m) => m.name).join(', ')
    text += ` (${modifierNames})`
  }

  text += ` — ${item.price * item.quantity} MAD`

  return text
}

// ============================================================================
// Helper: Build complete WhatsApp order message
// ============================================================================

export function buildWhatsAppMessage(
  cart: Cart,
  businessName: string,
  language: 'fr' | 'ar' | 'en'
): string {
  const emoji = '🧾'

  let message = `${emoji} `

  switch (language) {
    case 'fr':
      message += `Nouvelle commande — ${businessName}\n\n`
      break
    case 'ar':
      message += `طلب جديد — ${businessName}\n\n`
      break
    case 'en':
      message += `New Order — ${businessName}\n\n`
      break
  }

  if (cart.tableNumber) {
    message += `Table: ${cart.tableNumber}\n\n`
  }

  cart.items.forEach((item) => {
    message += formatCartItemForWhatsApp(item) + '\n'
  })

  message += '\n'

  if (cart.note) {
    message += `Note: ${cart.note}\n\n`
  }

  switch (language) {
    case 'fr':
      message += `Total: ${cart.subtotal} MAD`
      break
    case 'ar':
      message += `المجموع: ${cart.subtotal} MAD`
      break
    case 'en':
      message += `Total: ${cart.subtotal} MAD`
      break
  }

  return message
}

// ============================================================================
// Helper: Create WhatsApp URL
// ============================================================================

export function createWhatsAppURL(phoneNumber: string, message: string): string {
  const encodedMessage = encodeURIComponent(message)
  return `https://wa.me/${sanitizePhoneNumber(phoneNumber)}?text=${encodedMessage}`
}
